import React, { memo, useMemo } from 'react';
import { StyledOpeningHoursTable, StyledOpeningHourTooltip } from './styled';
import { Text, TextVariants } from '~/shared/components';
import { rotateArray, useTranslation } from '~/shared/utils';
import { OpeningHoursViewModel } from '~/lib/data-contract';

type OpeningHoursTableProps = {
    openingHours?: OpeningHoursViewModel[];
    textVariantCaption?: TextVariants | null;
    textVariantContent?: TextVariants;
    days?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 'immediate-days';
};

export const OpeningHoursTable = memo(
    ({
        openingHours,
        textVariantContent = 'body',
        textVariantCaption = 'display4',
        days = 7,
    }: OpeningHoursTableProps) => {
        const { translate } = useTranslation();

        // ISO standard for getDay() starts day with sunday = 0, but the data we get has monday = 0, so we shift one backwards.
        const day = (new Date().getDay() + 6) % 7;

        // Sort days so the current day is the first.
        // This is because the opening hours shouldn't show dates that have expired.
        const sortedOpeningHours = useMemo(() => rotateArray(openingHours || [], day), [
            openingHours,
            day,
        ]).slice(0, days === 'immediate-days' ? 2 : days);

        if (!openingHours?.length) {
            return null;
        }

        const immediateDaysTranlations = [
            translate('stores.page.openingHoursToday'),
            translate('stores.page.openingHoursTomorrow'),
        ];

        return (
            <StyledOpeningHoursTable data-chromatic="ignore">
                {textVariantCaption !== null && (
                    <Text variant={textVariantCaption} as="caption">
                        {translate('stores.page.openingHoursHeadline')}
                    </Text>
                )}
                <tbody>
                    {sortedOpeningHours.map(
                        ({ day, openingTime, closingTime, date, isClosed }, i) => (
                            <tr key={i}>
                                <StyledOpeningHourTooltip as="td" content={date} distance={-70}>
                                    <Text as="span" variant={textVariantContent}>
                                        {(days === 'immediate-days' &&
                                            immediateDaysTranlations[i]) ||
                                            day}
                                        :
                                    </Text>
                                </StyledOpeningHourTooltip>
                                <Text as="td" variant={textVariantContent}>
                                    {isClosed ? (
                                        translate('stores.page.openingHoursClosed')
                                    ) : (
                                        <>
                                            <time dateTime={`${date}T${openingTime}`}>
                                                {openingTime}
                                            </time>
                                            {` - `}
                                            <time dateTime={`${date}T${closingTime}`}>
                                                {closingTime}
                                            </time>
                                        </>
                                    )}
                                </Text>
                            </tr>
                        )
                    )}
                </tbody>
            </StyledOpeningHoursTable>
        );
    }
);
